import { CommonModule } from '@angular/common';
import {
  Component,
  effect,
  inject,
  Injector,
  OnInit,
  signal
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { OverlayContainer } from '@angular/cdk/overlay';
import {
  Router,
  RouterOutlet,
  NavigationEnd
} from '@angular/router';
import { filter } from 'rxjs';

import {
  tokenSignal,
  initializeAppState,
  setApiServiceForTokenRefresh,
  agentObjectSignal
} from './core/constants/auth-state';

import { AuthService } from './core/services/auth.service';
import { I18nService } from './core/services/i18n.service';
import { InactivityService } from './core/services/inactivity.service';
import { Shared } from './core/services/shared';
import { ApiService } from './core/services/api.service';
import { UserService } from './core/services/user.service';
import { environment } from '../environments/environment';
import { AuthProviderType } from './core/auth/auth.constants';

import { LoaderComponent } from './shared/loader/loader.component';
import { ToasterComponent } from './shared/toaster/toaster.component';

@Component({
  selector: 'app-root',
  imports: [
    CommonModule,
    FormsModule,
    LoaderComponent,
    ToasterComponent,
    RouterOutlet
  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent implements OnInit {

  private readonly authService = inject(AuthService);
  private readonly overlayContainer = inject(OverlayContainer);
  private readonly router = inject(Router);
  private readonly inactivityService = inject(InactivityService);
  private readonly shared = inject(Shared);
  private readonly apiService = inject(ApiService);
  private readonly userService = inject(UserService);
  private readonly injector = inject(Injector);

  protected i18n = inject(I18nService);

  protected title = signal('InsureLiv');
  protected prompt = signal('');
  protected response = signal('');
  protected loading = signal(false);

  ngOnInit(): void {

    // Initialize application state
    initializeAppState(this.authService.isAuthenticated());

    // Required only for legacy backend refresh-token flow.
    if (environment.auth.provider !== AuthProviderType.Keycloak) {
      setApiServiceForTokenRefresh(this.apiService);
    }

    // Apply Material theme to overlay components
    this.overlayContainer
      .getContainerElement()
      .classList.add('material-theme');

    this.setupCrossTabLogout();

    this.setupCurrentUserProfileSync();

    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => {

        const isLoggedIn = this.authService.isAuthenticated();

        const isLoginPage =
          this.router.url.includes('/login');

        if (isLoggedIn && !isLoginPage) {
          this.inactivityService.startWatching();
        } else {
          this.inactivityService.stopWatching();
        }

      });

  }

  private setupCurrentUserProfileSync(): void {

    effect(() => {

      // Provider authenticated users (Entra/Keycloak)
      // already load their profile during AuthService.initialize()
      if (this.authService.isAuthenticated()) {
        return;
      }

      const token = tokenSignal();

      const agentId =
        agentObjectSignal()?.agentId;

      if (!token || !agentId) {
        this.userService.currentUserProfile.set(null);
        return;
      }

      this.userService.syncCurrentUserProfile();

    }, { injector: this.injector });

  }

  private setupCrossTabLogout(): void {

    if (typeof window !== 'undefined' && window.addEventListener) {

      window.addEventListener(
        'storage',
        (event: StorageEvent) => {

          if (
            event.key === 'authToken' &&
            event.oldValue !== null &&
            event.newValue === null
          ) {
            void this.authService.handleExternalLogout();

          }

        });

    }

  }

}